import { useRef, useState } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { Camera } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AnimatePresence } from 'framer-motion'
import { ImageCropper } from './ImageCropper'

export function AvatarUpload({ size = 'w-16 h-16', shape = 'rounded-2xl', textSize = 'text-xl' }) {
  const { currentUser, uploadAvatar } = useAppStore()
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [cropSrc, setCropSrc] = useState(null)
  const rawObjUrl = useRef(null)
  const inputRef = useRef()

  function handleFileChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (rawObjUrl.current) URL.revokeObjectURL(rawObjUrl.current)
    rawObjUrl.current = URL.createObjectURL(file)
    setCropSrc(rawObjUrl.current)
    e.target.value = ''
  }

  async function handleCrop(blob) {
    setCropSrc(null)
    const previewUrl = URL.createObjectURL(blob)
    setPreview(previewUrl)
    setUploading(true)
    await uploadAvatar(blob)
    setUploading(false)
    setTimeout(() => {
      setPreview(null)
      URL.revokeObjectURL(previewUrl)
    }, 1000)
    if (rawObjUrl.current) {
      URL.revokeObjectURL(rawObjUrl.current)
      rawObjUrl.current = null
    }
  }

  function handleCancel() {
    setCropSrc(null)
    if (rawObjUrl.current) {
      URL.revokeObjectURL(rawObjUrl.current)
      rawObjUrl.current = null
    }
  }

  const src = preview || currentUser?.avatar
  const initials = (currentUser?.name || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={cn(
          'relative overflow-hidden group flex-shrink-0 transition-transform active:scale-[0.97]',
          size,
          shape,
        )}
        aria-label="Change profile photo"
      >
        {src ? (
          <img src={src} alt={currentUser?.name} className="w-full h-full object-cover" />
        ) : (
          <div
            className={cn('w-full h-full flex items-center justify-center font-bold', textSize)}
            style={{
              background: 'linear-gradient(135deg, rgba(244,136,154,0.3), rgba(167,139,250,0.2))',
              color: '#e63946',
            }}
          >
            {initials}
          </div>
        )}

        {/* Hover / loading overlay */}
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-black/45 transition-opacity duration-150',
            uploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          )}
        >
          {uploading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Camera size={15} className="text-white drop-shadow" />
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          tabIndex={-1}
        />
      </button>

      <AnimatePresence>
        {cropSrc && (
          <ImageCropper src={cropSrc} onCrop={handleCrop} onCancel={handleCancel} />
        )}
      </AnimatePresence>
    </>
  )
}
